import type { GrantBasis, UpdateLeavePolicyRequest } from '$lib/api/types';

export interface LeavePolicyFormValues {
	grantBasis: string;
	fiscalYearStartMonth: number | string;
	carryoverEnabled: boolean;
	carryoverMaxDays: number | string;
	advanceUsageEnabled: boolean;
	advanceUsageMaxDays: number | string;
}

export type LeavePolicyFormResult =
	| { ok: true; request: UpdateLeavePolicyRequest }
	| { ok: false; message: string };

const GRANT_BASES = ['FISCAL_YEAR', 'JOIN_DATE'] as const;
const MAX_POLICY_DAYS = 365;

export function validateLeavePolicyForm(values: LeavePolicyFormValues): LeavePolicyFormResult {
	if (!GRANT_BASES.includes(values.grantBasis as GrantBasis)) {
		return { ok: false, message: '부여 기준을 다시 선택해주세요.' };
	}

	const fiscalYearStartMonth = parseInteger(values.fiscalYearStartMonth);
	if (fiscalYearStartMonth === null || fiscalYearStartMonth < 1 || fiscalYearStartMonth > 12) {
		return { ok: false, message: '회계연도 시작월은 1월부터 12월 사이로 선택해주세요.' };
	}

	const carryoverMaxDays = parseCarryoverMaxDays(values);
	if (!carryoverMaxDays.ok) {
		return carryoverMaxDays;
	}
	const advanceUsageMaxDays = parseAdvanceUsageMaxDays(values);
	if (!advanceUsageMaxDays.ok) {
		return advanceUsageMaxDays;
	}

	return {
		ok: true,
		request: {
			grantBasis: values.grantBasis as GrantBasis,
			fiscalYearStartMonth,
			carryoverEnabled: values.carryoverEnabled,
			carryoverMaxDays: carryoverMaxDays.value,
			advanceUsageEnabled: values.advanceUsageEnabled,
			advanceUsageMaxDays: advanceUsageMaxDays.value
		}
	};
}

function parseCarryoverMaxDays(
	values: LeavePolicyFormValues
): { ok: true; value: number | null } | { ok: false; message: string } {
	if (!values.carryoverEnabled) {
		return { ok: true, value: null };
	}

	return parseOptionalPolicyDays(values.carryoverMaxDays, '이월 상한은 0일 이상 365일 이하로 입력해주세요.');
}

function parseAdvanceUsageMaxDays(
	values: LeavePolicyFormValues
): { ok: true; value: number | null } | { ok: false; message: string } {
	if (!values.advanceUsageEnabled) {
		return { ok: true, value: null };
	}

	return parseOptionalPolicyDays(
		values.advanceUsageMaxDays,
		'연차 선사용 한도는 0일 이상 365일 이하로 입력해주세요.'
	);
}

function parseOptionalPolicyDays(
	value: number | string,
	message: string
): { ok: true; value: number | null } | { ok: false; message: string } {
	const raw = String(value).trim();
	if (!raw) {
		return { ok: true, value: null };
	}

	const parsed = Number(raw);
	if (!Number.isFinite(parsed) || parsed < 0 || parsed > MAX_POLICY_DAYS) {
		return { ok: false, message };
	}

	return { ok: true, value: parsed };
}

function parseInteger(value: number | string): number | null {
	const parsed = typeof value === 'number' ? value : Number(String(value).trim());
	return Number.isInteger(parsed) ? parsed : null;
}
